import { of, throwError } from 'rxjs';
import { IDashboardVehicle } from '../../shared/models/dashboard.model';
import * as fromActions from './bdcdashboard.actions';
// import { deepCopy } from '../../shared/services/util.service';
import { AppointmentStatus, IAppointment, IResponse, IVehicle, SourceSystem } from '@signal/asp-data-commons';
import * as _ from 'lodash';
import { Predicate } from '@ngrx/entity';
import { DateTime } from 'luxon';

/* Projector functions */

// /**
//  * Converts the UserVehicle response from the DCS API to simplified Vehicle interface for ASP Application
//  * @param userVehicle raw response from the DCS API
//  */
// export const toVehiclesProjector = (userVehicle: UserVehicle) => {
//   /* DCS Response gives status code as string, hence converting it to number using '+' operator */
//   if (+userVehicle.status.code === 200) {
//     return userVehicle.vehicles.map(v => getVehicle(v));
//   } else {
//     return throwError(`${userVehicle.status.code} - ${userVehicle.status.messages.join(', ')}`);
//   }
// };

export const toAppointmentsProjector = (appointments: IResponse<IAppointment[]>) => {
  if (appointments.status.code === 200) {
    return of(appointments.data);
  } else {
    return throwError(`${appointments.status.code} - ${appointments.status.messages.join(', ')}`);
  }
};

export const appointmentsToDashboardVehiclesProjector = (appointments: IAppointment[]): IDashboardVehicle[] => {
  return appointments.map(appointment => ({
    ...appointment.vehicle,
    appointment
  }));
};

export const ownVehicleAppointmentsToDashboardVehiclesProjector =
  (appointments: IAppointment[], userVehicles: IVehicle[]): IDashboardVehicle[] => {
    return appointments.map(appointment => ({
      ...appointment.vehicle,
      vehicleId: userVehicles.find(x => x.vin === appointment.vehicle.vin)?.vehicleId,
      appointment
    }));
  };

export const vehiclesToDashboardVehiclesProjector = (vehicles: IVehicle[]): IDashboardVehicle[] => {
  return vehicles.map(vehicle => ({
    ...vehicle,
    isOwnVehicle: true,
    appointment: undefined
  }));
};

export const apptToDashboardVehicleProjector =
  (appointments: IAppointment[]) =>
    appointmentsToDashboardVehiclesProjector(appointments);

export const mergeUserVehiclesAndAppointmentsProjector =
  (userVehicles: IVehicle[], serviceAppointments: IAppointment[]) =>
    mergeUserVehiclesAndAppointments(userVehicles, serviceAppointments);


// export const copyUserVehiclesProjector = (userVehicles: IVehicle[]) => {
//   return userVehicles.map(deepCopy(userVehicles));
// };



export const updateAppointmentAsCancelling = (action: fromActions.BdcDashboardActions) => a => {
  const payloadAppointmentId = action instanceof fromActions.CancelAppointment ? action.payload.appointment.appointmentId : '';
  a.appointmentStatus = a.appointmentId === payloadAppointmentId ? AppointmentStatus.CANCELLING : a.appointmentStatus;
  return a;
};

/* Reducer helper functions */

export const mergeUserVehiclesAndAppointments = (userVehicles: IVehicle[], appointments: IAppointment[]): IDashboardVehicle[] => {
  const ownVehicleAppointments = appointments.filter(a => a.vehicle.isOwnVehicle);
  const otherVehicleAppointments = appointments.filter(a => !a.vehicle.isOwnVehicle);

  /* Find vehicles without appointments and convert them into IDashboardVehicle */
  const ownVehicleApptVINs = ownVehicleAppointments.map(o => o.vehicle.vin);
  const ownVehiclesWithoutAppointments = userVehicles?.filter(v => !ownVehicleApptVINs.includes(v.vin));
  return [
    ...ownVehicleAppointmentsToDashboardVehiclesProjector(ownVehicleAppointments, userVehicles),
    ...appointmentsToDashboardVehiclesProjector(otherVehicleAppointments),
    ...vehiclesToDashboardVehiclesProjector(ownVehiclesWithoutAppointments),
  ];
};
export const getAllVehiclesAppointments = (userVehicles: IVehicle[], appointments: IAppointment[]): IDashboardVehicle[] => {
  const ownVehicleAppointments = appointments?.filter(a => a.vehicle.isOwnVehicle);
  const otherVehicleAppointments = appointments?.filter(a => !a.vehicle.isOwnVehicle);
  return [
    ...ownVehicleAppointmentsToDashboardVehiclesProjector(ownVehicleAppointments, userVehicles),
    ...appointmentsToDashboardVehiclesProjector(otherVehicleAppointments)
  ]
}

export const getFilterAppointmentByDate = (filter, appt) => {
  const values = filter?.split('/');
  const dateFormat = 'yyyy-MM-dd';
  const month = Number(values[0]) || 0, day = Number(values[1]) || 0, year = Number(values[2]) || 0;
  const predicate: Predicate<IAppointment> = (x: IAppointment) => month && day && year ? 
  (x.appointmentStartDate.indexOf(DateTime.local(year, month, day).toFormat(dateFormat)) >= 0) : 
  (month && day ?  (DateTime.fromFormat(x.appointmentStartDate, dateFormat).get('month') === month && DateTime.fromFormat(x.appointmentStartDate,'yyyy-MM-dd').get('day') === day) :
  month ? DateTime.fromFormat(x.appointmentStartDate, dateFormat).get('month') === month : false)
  return _.filter(appt, predicate);
}

export const getFilteredAppointment = (appt, field, filter) => {
  switch (field) {
    case 'customerNameFilter':
      appt = _.filter(appt, obj => _.some(filter, (f) => `${obj?.customer?.firstName.toUpperCase()}${obj?.customer?.lastName.toUpperCase()}`.replace(/ /gi, '').includes(f.replace(/ /gi, ''))));
      break;

    case 'contactInfoFilter':
      appt = _.filter(appt, obj => _.some(filter, (f) => (obj.customer?.emailAddress?.toUpperCase()?.includes(f) || obj.customer?.phoneNumber?.toUpperCase()?.includes(f))));
      break;

    case 'modelFilter':
      appt = _.filter(appt, obj => _.some(filter, (f) => obj.vehicle.model?.replace(/ /gi, '')?.toUpperCase().includes(f)));
      break;

    case 'vinFilter':
      appt = _.filter(appt, obj => _.some(filter, (f) => obj.vehicle.vin.toUpperCase().includes(f)))
      break;

    case 'appointmentStartDateFilter':
      appt = getFilterAppointmentByDate(filter, appt);
      break;

    case 'sourceFilter':
      appt = _.filter(appt, obj => _.some(filter, (f) => obj.vehicle?.source?.toUpperCase().includes((f === 'NEW' ? SourceSystem.NVI : f === 'USED' ? SourceSystem.UVI : f))))
      break;

    default:
      return appt
  }

  return appt;
}

export const getSortedArray = (field, order, appt) => {
  switch (field) {
    case 'customerName':
      appt = _.orderBy(appt, [(obj: any) => obj.customer?.firstName.toUpperCase()], [order])
      break;

    case 'contactInfo':
      appt = _.orderBy(appt, [(obj: any) => obj.customer?.emailAddress?.toUpperCase()], [order])
      break;

    case 'model':
      appt = _.orderBy(appt, [(obj: any) => obj.vehicle?.model.toUpperCase()], [order])
      break;

    case 'vin':
      appt = _.orderBy(appt, [(obj: any) => obj.vehicle?.vin.toUpperCase()], [order])
      break;

    case 'appointmentStartDate':
      appt = _.orderBy(appt, ['appointmentStartDate'], [order])
      break;

    case 'source':
      appt = _.orderBy(appt, [(obj: any) => obj.vehicle?.source?.toUpperCase()], [order])
      break;

    default:
      return appt
  }

  return appt;
}

export const getSearchAndSortedData = (appt: IAppointment[], searchConfig, sortingField: string, sortOrder: string, isSearch: boolean) => {
  if (isSearch) {
    const filters = ['customerNameFilter', 'contactInfoFilter', 'modelFilter', 'vinFilter', 'sourceFilter'];
    filters.forEach(filter => {
      const value = searchConfig[filter];
      if (value?.length && appt.length) {
        const values = value.toUpperCase().split(',');
        const data = [];
        values.forEach(x => data.push(x.replace(/ /gi, '')));
        appt = getFilteredAppointment(appt, filter, data);
      }
    })
    if(searchConfig['appointmentStartDateFilter']) {
      appt = getFilteredAppointment(appt, 'appointmentStartDateFilter', searchConfig['appointmentStartDateFilter']);
    }
  }
  if (sortingField?.length && sortOrder?.length && appt.length) {
    appt = getSortedArray(sortingField, sortOrder.toLowerCase(), appt);
  }
  return appt;
}
